import React, { useCallback, useEffect, useState } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from 'src/firebase/firebaseConfig';
import { FirebaseEvent, ClientFormInstance } from 'shared/types/appTypes';
import { useSession } from 'src/SessionContext';
import ContainerSpinner from 'src/ui-components/ContainerSpinner';
import { Box, Typography, Card, CardContent, CardActionArea, Stack, Chip, Container, Button } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ClientEventFormDialog from './ClientEventFormDialog';
import { formatDateTzNaive } from 'src/utils/formatDateTzNaive';
import { getFormCompletionStatus } from 'src/utils/clientFormUtils';
import { useNavigate, useLocation } from 'react-router';

interface ClientViewProps {
  clientId: string;
}

const ClientView: React.FC<ClientViewProps> = ({ clientId }) => {
  const { session } = useSession() || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<FirebaseEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<FirebaseEvent | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialQuestionIndex, setInitialQuestionIndex] = useState<number | undefined>(undefined);
  const [formCompletions, setFormCompletions] = useState<Record<string, { filledCount: number; totalCount: number }>>({});
  const [formVisited, setFormVisited] = useState<Record<string, boolean>>({});

  const refreshFormCompletion = useCallback(async (eventId: string) => {
    try {
      const formDoc = await getDoc(doc(db, 'clientForms', eventId));
      if (!formDoc.exists()) {
        setFormCompletions((prev) => {
          const { [eventId]: _removed, ...rest } = prev;
          return rest;
        });
        return;
      }

      const formData = formDoc.data() as ClientFormInstance;
      setFormCompletions((prev) => ({
        ...prev,
        [eventId]: getFormCompletionStatus(formData),
      }));
      // Also refresh visited state (clientLastOpenedAt is set when form loads)
      if (formData.clientLastOpenedAt) {
        setFormVisited((prev) => ({ ...prev, [eventId]: true }));
      }
    } catch (err) {
      console.error('Error refreshing form completion for event:', eventId, err);
    }
  }, []);

  const handleEventClick = (event: FirebaseEvent) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleDialogClose = useCallback(() => {
    const selectedEventId = selectedEvent?.id;
    setDialogOpen(false);
    setSelectedEvent(null);
    setInitialQuestionIndex(undefined);
    if (selectedEventId) {
      void refreshFormCompletion(selectedEventId);
    }
  }, [refreshFormCompletion, selectedEvent]);

  useEffect(() => {
    const fetchClientEvents = async () => {
      try {
        const eventsRef = collection(db, 'events');
        const q = query(eventsRef, where('clientAccess', 'array-contains', clientId));
        const querySnapshot = await getDocs(q);

        const clientEvents: FirebaseEvent[] = [];
        querySnapshot.forEach((doc) => {
          clientEvents.push({ id: doc.id, ...doc.data() } as FirebaseEvent);
        });

        // Sort by date
        clientEvents.sort((a, b) => {
          const dateA = a.dateISO8601 || '';
          const dateB = b.dateISO8601 || '';
          return dateA.localeCompare(dateB);
        });

        setEvents(clientEvents);

        // Fetch form completion status and visited state for each event
        const completions: Record<string, { filledCount: number; totalCount: number }> = {};
        const visited: Record<string, boolean> = {};
        await Promise.all(
          clientEvents.map(async (event) => {
            try {
              const formDoc = await getDoc(doc(db, 'clientForms', event.id));
              if (formDoc.exists()) {
                const formData = formDoc.data() as ClientFormInstance;
                completions[event.id] = getFormCompletionStatus(formData);
                if (formData.clientLastOpenedAt) {
                  visited[event.id] = true;
                }
              }
            } catch (err) {
              console.error('Error fetching form for event:', event.id, err);
            }
          })
        );
        setFormCompletions(completions);
        setFormVisited(visited);
      } catch (error) {
        console.error('Error fetching client events:', error);
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      fetchClientEvents();
    }
  }, [clientId]);

  // Handle deep-link state from summary page
  useEffect(() => {
    const state = location.state as { openEventId?: string; initialQuestionIndex?: number } | null;
    if (state?.openEventId && events.length > 0) {
      const targetEvent = events.find((e) => e.id === state.openEventId);
      if (targetEvent) {
        setSelectedEvent(targetEvent);
        setInitialQuestionIndex(state.initialQuestionIndex);
        setDialogOpen(true);
      }
      // Clear the state so it doesn't re-trigger on back navigation
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [events, location.state, navigate, location.pathname]);

  if (loading) {
    return <ContainerSpinner />;
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>
        Welcome, {session?.user?.name || 'Client'}!
      </Typography>

      <Typography variant="h6" sx={{ mb: 2 }}>
        Your Events
      </Typography>

      {events.length === 0 ? (
        <Typography color="text.secondary">No events found. Please contact your event coordinator.</Typography>
      ) : (
        <Stack spacing={2}>
          {events.map((event) => (
            <Card key={event.id} variant="outlined">
              <CardActionArea onClick={() => handleEventClick(event)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <EventIcon color="primary" />
                    <Typography variant="h6">
                      {event.friendlyTitle || event.dayliteData?.name || 'Untitled Event'}
                    </Typography>
                  </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                    {event.dateISO8601 && <Chip label={formatDateTzNaive(event.dateISO8601)} size="small" />}
                    {event.venueInfo && <Chip label={event.venueInfo} size="small" variant="outlined" />}
                    {formCompletions[event.id] && formCompletions[event.id].totalCount > 0 && (
                      <Chip
                      label={`${formCompletions[event.id].filledCount} / ${formCompletions[event.id].totalCount} answered`}
                      size="small"
                      color={formCompletions[event.id].filledCount === formCompletions[event.id].totalCount ? 'success' : 'default'}
                      variant="outlined"
                      />
                    )}
                    </Stack>
                </CardContent>
              </CardActionArea>
              {formVisited[event.id] && (
                <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    startIcon={<SummarizeIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/client/form-summary/${event.id}`);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    View Summary
                  </Button>
                </Box>
              )}
            </Card>
          ))}
        </Stack>
      )}

      <ClientEventFormDialog open={dialogOpen} onClose={handleDialogClose} event={selectedEvent} initialQuestionIndex={initialQuestionIndex} />
    </Container>
  );
};

export default ClientView;
